import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { ASSETS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Home, FileText, Search, Users, CheckCircle2, ShieldCheck, LogIn, Lock } from "lucide-react";
import Header from "@/components/Header";
import EmpreendimentoWidget from "@/components/EmpreendimentoWidget";
import InformacoesWidget from "@/components/InformacoesWidget";
import PontosApoioWidget from "@/components/PontosApoioWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_DOCS = ["Cadastro Único (CadÚnico)","CPF","Documento de Identidade (RG)","Comprovante de Residência","Título de Eleitor","Certidão de Casamento (se aplicável)","Laudo PCD (se aplicável)"];

export default function HomePage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState(DEFAULT_DOCS);
  const [periodo, setPeriodo] = useState(null);
  useEffect(() => {
    axios.get(`${API}/config/documentos-obrigatorios`)
      .then((r) => setDocs(r.data.items?.length ? r.data.items : DEFAULT_DOCS))
      .catch(() => setDocs(DEFAULT_DOCS));
    axios.get(`${API}/config/periodo-inscricoes`)
      .then((r) => setPeriodo(r.data))
      .catch(() => {});
  }, []);
  const aberto = !periodo || periodo.status?.aberto;
  const badgeText = (() => {
    if (!periodo) return "";
    if (periodo.status?.aberto) return "Inscrições Abertas";
    if (periodo.status?.motivo === "encerrado") return "Inscrições Encerradas";
    if (periodo.status?.motivo === "nao_iniciado") return "Inscrições em Breve";
    return "Inscrições Desativadas";
  })();
  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      {/* Hero */}
      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${ASSETS.banner})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#083CA6]/95 via-[#083CA6]/85 to-[#00883A]/75" />
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold mb-5 border border-white/20">
              <ShieldCheck className="w-3.5 h-3.5" /> PREFEITURA MUNICIPAL DE GRAJAÚ – MA
            </div>
            {periodo && badgeText && (
              <div
                className={`inline-flex items-center gap-2 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold mb-3 ml-2 border ${
                  aberto ? "bg-emerald-500/25 border-emerald-300/50 text-emerald-50"
                          : "bg-rose-500/25 border-rose-300/50 text-rose-50"
                }`}
                data-testid="hero-periodo-badge"
              >
                {badgeText}
                {periodo.fim && aberto && (
                  <span className="ml-1 opacity-90">até {new Date(periodo.fim).toLocaleDateString("pt-BR")}</span>
                )}
              </div>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4 leading-tight">
              Pré-Inscrição<br/>
              <span className="text-yellow-300">Minha Casa Minha Vida 2026</span>
            </h1>
            <p className="text-base md:text-lg text-white/90 mb-8 max-w-2xl">
              Cadastre-se para participar da seleção habitacional do município de Grajaú. Inscrição online, rápida e segura.
            </p>
            <div className="flex flex-wrap gap-3">
              {aberto ? (
                <Button size="lg" className="bg-yellow-400 hover:bg-yellow-300 text-[#083CA6] font-bold" onClick={() => navigate("/inscricao")} data-testid="cta-fazer-inscricao">
                  <FileText className="w-5 h-5 mr-2" /> Fazer Inscrição
                </Button>
              ) : (
                <Button size="lg" disabled aria-disabled="true" title="O período de inscrições está desativado" className="bg-slate-300/40 text-white/70 font-bold cursor-not-allowed border border-white/20" data-testid="cta-fazer-inscricao-bloqueado">
                  <Lock className="w-5 h-5 mr-2" /> {badgeText || "Inscrições Indisponíveis"}
                </Button>
              )}
              <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur border-white/40 text-white hover:bg-white hover:text-[#083CA6]" onClick={() => navigate("/consultar")} data-testid="cta-consultar">
                <Search className="w-5 h-5 mr-2" /> Consultar Inscrição
              </Button>
              <Button size="lg" variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate("/login")} data-testid="cta-login">
                <LogIn className="w-5 h-5 mr-2" /> Entrar no Sistema
              </Button>
            </div>
          </div>
        </div>
      </section>

      <EmpreendimentoWidget />

      {/* Regras */}
      <section className="max-w-7xl mx-auto px-4 py-14" data-testid="regras-section">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-wider text-[#00883A] font-bold mb-1">Quem pode participar?</div>
          <h2 className="text-3xl md:text-4xl font-black text-[#0F172A]">Regras de Participação</h2>
          <div className="h-1 w-16 bg-[#083CA6] mt-3" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: <Home className="w-6 h-6" />, t: "Renda até R$ 3.200", d: "Famílias com renda bruta mensal de até R$ 3.200,00 (Faixa 1)." },
            { icon: <FileText className="w-6 h-6" />, t: "Cadastro Único", d: "Estar inscrito no CadÚnico do município." },
            { icon: <Users className="w-6 h-6" />, t: "Residente em Grajaú", d: "Comprovar residência no município de Grajaú-MA." },
            { icon: <CheckCircle2 className="w-6 h-6" />, t: "Sem imóvel próprio", d: "Não ter sido beneficiado anteriormente por programa habitacional federal." },
          ].map((r) => (
            <Card key={r.t} className="border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="w-11 h-11 rounded-md bg-[#083CA6]/10 text-[#083CA6] flex items-center justify-center mb-3">{r.icon}</div>
                <h3 className="font-bold text-base mb-1">{r.t}</h3>
                <p className="text-sm text-slate-600">{r.d}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 p-5 bg-yellow-50 border-l-4 border-yellow-500 rounded-r">
          <p className="text-sm text-slate-800">
            <strong>Aviso importante:</strong> A inscrição online não garante a contemplação no programa.
            Os dados serão analisados pela equipe técnica da Prefeitura Municipal de Grajaú.
          </p>
        </div>
      </section>

      <InformacoesWidget />

      <PontosApoioWidget />

      {/* Documentação */}
      <section className="bg-white border-t border-slate-200 py-14" data-testid="docs-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#00883A] font-bold mb-1">Documentação Obrigatória</div>
              <h2 className="text-3xl md:text-4xl font-black mb-4">Documentos necessários</h2>
              <ul className="space-y-2.5 text-sm text-slate-700" data-testid="docs-list">
                {docs.map((it) => (
                  <li key={it} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#00883A] mt-0.5 flex-shrink-0" /> {it}</li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#083CA6] to-[#00883A] p-8 rounded-lg text-white">
              <h3 className="text-2xl font-bold mb-2">{aberto ? "Pronto para começar?" : "Inscrições indisponíveis"}</h3>
              <p className="text-white/90 text-sm mb-5">
                {aberto
                  ? "Tenha os documentos digitalizados em PDF, JPG ou PNG (até 20MB cada)."
                  : "O período de pré-inscrições está desativado no momento. Fique atento aos comunicados oficiais da Prefeitura."}
              </p>
              {aberto ? (
                <Button size="lg" className="bg-yellow-400 hover:bg-yellow-300 text-[#083CA6] font-bold" onClick={() => navigate("/inscricao")} data-testid="cta-comecar">
                  Iniciar Inscrição
                </Button>
              ) : (
                <Button size="lg" disabled aria-disabled="true" title="O período de inscrições está desativado" className="bg-white/20 text-white/80 font-bold cursor-not-allowed border border-white/30" data-testid="cta-comecar-bloqueado">
                  <Lock className="w-5 h-5 mr-2" /> {badgeText || "Inscrições Desativadas"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#0F172A] text-slate-300 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-xs flex flex-wrap items-center justify-between gap-3">
          <div>© 2026 Prefeitura Municipal de Grajaú – MA · SEHAB</div>
          <div>Sistema de Pré-Inscrição MCMV 2026</div>
        </div>
      </footer>
    </div>
  );
}
